/**
 * useFleetAttention (FT-441 server fix) — VESSELS-ONLY SLICE. Resolve per-vessel
 * attention for the Fleet landing via ONE batched server call.
 *
 * The attention engine (src/utils/fleetAttention.js) provisions its data
 * SERVER-SIDE: a single GET /api/fleet/attention-data returns the raw rows for
 * every assigned vessel, which the engine unwraps and derives with its canonical
 * (single-source-of-truth) rules. This hook drives that one call for the handed
 * vessel-id set and exposes a per-vessel result map so each card renders its OWN
 * state:
 *   - undefined      → still checking (neutral placeholder, NOT "all up to date")
 *   - { unknown }    → could not resolve (call failed — INCLUDING a 404 while the
 *                      endpoint is not yet deployed — or vessel absent from the
 *                      authoritative response) → "Checking…"
 *   - { overdueMaintenance, expiringCerts, hasAttention } → resolved truth
 *
 * SAFETY (FT-441 — never a false all-clear): if the batched call fails,
 * `fetchFleetAttention` resolves EVERY requested id to `{ unknown }`; an id the
 * server did not return is also `{ unknown }`. A confident "all up to date" is
 * only ever produced from an id the authoritative server response INCLUDED with
 * empty collections. There is no code path that turns a failure into a clear.
 *
 * PERMISSION SAFETY (FT-435 — do NOT regress): the hook renders ONLY the ids it
 * is handed (the already server-scoped VesselContext list), and the endpoint
 * independently re-scopes server-side (user_vessel_access). No scope is widened.
 *
 * PERFORMANCE: ONE network call for the whole fleet. The effect only re-runs when
 * the SET of ids changes (stable string key), so re-renders don't re-fetch.
 *
 * NO FACILITY PATH: this vessels-only slice resolves vessel attention only.
 */

import { useEffect, useRef, useState } from 'react';
import { fetchFleetAttention } from '../../../utils/fleetAttention';

/**
 * @param {string[]} vesselIds server-scoped assigned vessel ids
 * @returns {{ vesselAttention: Record<string, object|undefined> }}
 */
export function useFleetAttention(vesselIds) {
  const [vesselAttention, setVesselAttention] = useState({});

  // Stable string key so the effect only re-runs when the SET of ids changes,
  // not on every render (the array is a new reference each render).
  const vKey = (vesselIds || []).filter(Boolean).join(',');

  // Guard against setState after unmount / id-set change (stale async resolves).
  const runIdRef = useRef(0);

  useEffect(() => {
    const myRun = runIdRef.current + 1;
    runIdRef.current = myRun;
    const isCancelled = () => runIdRef.current !== myRun;

    const vIds = vKey ? vKey.split(',') : [];

    // Reset to "checking" for the current id set (undefined per id).
    setVesselAttention(Object.fromEntries(vIds.map((id) => [id, undefined])));

    // Nothing to resolve — leave the (empty) checking map.
    if (vIds.length === 0) {
      return undefined;
    }

    // ONE batched call for the whole fleet. fetchFleetAttention never rejects —
    // on any failure it resolves every requested id to { unknown }.
    fetchFleetAttention(vIds).then(({ vessels }) => {
      if (isCancelled()) return;
      setVesselAttention(vessels);
    });

    return () => {
      // Invalidate this run so an in-flight resolve is dropped.
      runIdRef.current = myRun + 1;
    };
  }, [vKey]);

  return { vesselAttention };
}

export default useFleetAttention;
